import { PublicKey, Transaction, VersionedTransaction } from '@solana/web3.js';
import { getWallets } from '@wallet-standard/app';
import type { Wallet } from '@wallet-standard/base';
import { captureSentryException, addSentryBreadcrumb } from '@/config/sentryConfig';

/**
 * Mobile Wallet Adapter using Wallet Standard Protocol
 *
 * This is the CORRECT implementation for web apps according to official
 * Solana Mobile documentation. It uses the Wallet Standard protocol via
 * @solana-mobile/wallet-standard-mobile package.
 *
 * Key differences from previous implementation:
 * - Uses getWallets() from Wallet Standard (not custom adapter instance)
 * - Wallets are registered via registerMwa() in mwaConfig.ts
 * - Detection works through Wallet Standard registry
 * - Compatible with Seekr and all MWA-compliant wallets
 *
 * How it works:
 * 1. registerMwa() is called in main.tsx to register MWA with Wallet Standard
 * 2. getMWAWallet() queries the Wallet Standard registry for MWA wallets
 * 3. Connection/signing uses the Wallet Standard feature API
 */

/**
 * Gets the first available MWA wallet from the Wallet Standard registry
 *
 * After registerMwa() is called, MWA wallets become available through
 * the Wallet Standard getWallets() API.
 *
 * @returns The MWA wallet or null if none found
 */
const getMWAWallet = (): Wallet | null => {
  try {
    const wallets = getWallets().get();

    // Find MWA wallet registered by registerMwa()
    // @solana-mobile/wallet-standard-mobile always registers under these exact names:
    // - "Mobile Wallet Adapter" (local/Android Intent connection)
    // - "Remote Mobile Wallet Adapter" (remote/QR code connection)
    // We match by exact name to avoid accidentally returning other wallets
    // (e.g. Backpack, Phantom) that also implement standard:connect.
    const mwaWallet = wallets.find((wallet) => {
      const walletName = wallet.name.toLowerCase();
      return (
        walletName === 'mobile wallet adapter' ||
        walletName === 'remote mobile wallet adapter'
      );
    });

    // Return null if no MWA wallet found — do NOT fall back to wallets[0]
    // which would silently return the first installed wallet (e.g. Backpack).
    if (!mwaWallet) {
      // Log all registered wallets to Sentry so we can debug detection misses
      addSentryBreadcrumb('MWA wallet not found in registry', {
        totalWallets: wallets.length,
        walletNames: wallets.map((w) => w.name),
        walletFeatureKeys: wallets.map((w) => Object.keys(w.features)),
        rawWallets: JSON.stringify(
          wallets.map((w) => ({
            name: w.name,
            version: w.version,
            chains: w.chains,
            features: Object.keys(w.features),
          }))
        ).slice(0, 1000),
      });
    }

    return mwaWallet || null;
  } catch (error) {
    console.error('[MWA] Failed to get MWA wallet:', error);

    // Report to Sentry - wallet detection failures are important
    captureSentryException(error as Error, {
      operation: 'mwa_wallet_detection',
      availableWalletsCount: getWallets().get().length,
    });

    return null;
  }
};

/**
 * Tracks the current MWA connection state
 */
let currentMWAWallet: {
  wallet: Wallet | null;
  publicKey: PublicKey | null;
  isConnected: boolean;
} = {
  wallet: null,
  publicKey: null,
  isConnected: false,
};

/**
 * Checks if MWA is currently available
 *
 * MWA is available if:
 * 1. registerMwa() was called (in mwaConfig.ts)
 * 2. At least one MWA wallet is in the Wallet Standard registry
 * 3. Platform supports MWA (checked separately)
 *
 * @returns boolean indicating if MWA wallets are available
 */
export const isMWAAvailable = (): boolean => {
  try {
    const wallet = getMWAWallet();
    return wallet !== null;
  } catch {
    return false;
  }
};

/**
 * Gets the current MWA connection state
 * Compatible with existing code that checks connection status
 */
export const getMWAConnectionState = (): {
  wallet: any | null;
  publicKey: PublicKey | null;
  isConnected: boolean;
} => {
  return currentMWAWallet;
};

/**
 * Provider interface compatible with existing WalletContext
 * Uses Wallet Standard protocol for all operations
 */
export const mwaProvider = {
  get publicKey(): PublicKey | null {
    return currentMWAWallet.publicKey;
  },

  get isConnected(): boolean {
    return currentMWAWallet.isConnected;
  },

  async connect(): Promise<{ publicKey: PublicKey }> {
    try {
      const wallet = getMWAWallet();

      if (!wallet) {
        throw new Error('No Solana wallets found. Please install a wallet like Phantom, Solflare, Seekr, or Ultimate.');
      }

      // Check if wallet has connect feature
      const connectFeature = wallet.features['standard:connect'];
      if (!connectFeature) {
        throw new Error('Wallet does not support connection');
      }

      // Connect using Wallet Standard
      // standard:connect returns { accounts: WalletAccount[] }, not a direct array
      const result = await (connectFeature as any).connect();

      // Log raw result structure to Sentry so we can inspect the accounts shape during debugging
      addSentryBreadcrumb('MWA connect() raw result', {
        resultType: typeof result,
        resultKeys: result ? Object.keys(result) : null,
        accountsIsArray: Array.isArray(result?.accounts),
        accountsCount: result?.accounts?.length ?? null,
        firstAccountKeys: result?.accounts?.[0] ? Object.keys(result.accounts[0]) : null,
        // Truncated raw dump - captures structure without leaking sensitive data
        rawResultSnippet: JSON.stringify(result).slice(0, 500),
      });

      const accounts = result?.accounts;

      if (!accounts || accounts.length === 0) {
        throw new Error('No accounts returned from wallet');
      }

      // Get the first account's public key
      const account = accounts[0];
      if (!account || !account.publicKey) {
        throw new Error('Invalid account data returned from wallet');
      }
      const publicKeyBytes = account.publicKey;
      const publicKey = new PublicKey(publicKeyBytes);

      // Store connection state
      currentMWAWallet = {
        wallet,
        publicKey,
        isConnected: true,
      };

      // Add breadcrumb for successful connection
      addSentryBreadcrumb('MWA wallet connected', {
        walletName: wallet.name,
        publicKey: publicKey.toBase58().slice(0, 8) + '...',  // Shortened for privacy
      });

      console.log('[MWA] Successfully connected to wallet:', wallet.name);
      return { publicKey };
    } catch (error: any) {
      console.error('[MWA] Connection error:', error);

      // Determine if this is a user cancellation or environment issue (don't report these)
      const isUserCancellation =
        error.message?.includes('User declined') ||
        error.message?.includes('User rejected') ||
        error.message?.includes('cancelled by user') ||
        // WebSocket failures mean the wallet app isn't open - not a code bug
        error.message?.includes('wallet websocket') ||
        error.message?.includes('ws://localhost');

      // Report to Sentry (skip user cancellations)
      if (!isUserCancellation) {
        const wallet = getMWAWallet();

        captureSentryException(error, {
          operation: 'mwa_connect',
          walletName: wallet?.name || 'unknown',
          availableFeatures: wallet ? Object.keys(wallet.features).join(', ') : 'none',
          errorType: error.name || 'Error',
          errorMessage: error.message,
          connectionState: {
            wasConnected: currentMWAWallet.isConnected,
            hadPublicKey: currentMWAWallet.publicKey !== null,
          },
        });
      }

      // Enhance error messages for better UX
      if (error.message?.includes('wallet websocket') || error.message?.includes('ws://localhost')) {
        // WebSocket error: wallet app is not open or not listening
        throw new Error('Could not reach your wallet app. Please open Seekr (or your Solana wallet) and try again.');
      } else if (error.message?.includes('Invalid account data') ||
          (error.message?.includes('cannot read') && error.message?.includes('publicKey'))) {
        throw new Error('Failed to retrieve account from wallet. Please try reconnecting.');
      } else if (error.message?.includes('User declined') || error.message?.includes('User rejected')) {
        throw new Error('Connection cancelled by user');
      } else if (error.message?.includes('No wallets found') || error.message?.includes('No Solana wallets')) {
        throw new Error('No Solana wallets found. Please install a wallet like Phantom, Solflare, Seekr, or Ultimate.');
      } else if (error.message?.includes('blocked')) {
        throw new Error('Pop-up blocked. Please enable pop-ups for this site to connect your wallet.');
      }
      throw error;
    }
  },

  async disconnect(): Promise<void> {
    try {
      if (currentMWAWallet.wallet && currentMWAWallet.wallet.features['standard:disconnect']) {
        const disconnectFeature = currentMWAWallet.wallet.features['standard:disconnect'];
        await (disconnectFeature as any).disconnect();
      }

      // Clear connection state
      currentMWAWallet = {
        wallet: null,
        publicKey: null,
        isConnected: false,
      };

      console.log('[MWA] Successfully disconnected');
    } catch (error) {
      console.error('[MWA] Disconnect error:', error);

      // Report disconnect errors to Sentry
      captureSentryException(error as Error, {
        operation: 'mwa_disconnect',
        walletName: currentMWAWallet.wallet?.name || 'unknown',
        wasConnected: currentMWAWallet.isConnected,
      });

      throw error;
    }
  },

  async signTransaction<T extends Transaction | VersionedTransaction>(
    transaction: T
  ): Promise<T> {
    try {
      if (!currentMWAWallet.wallet || !currentMWAWallet.isConnected) {
        throw new Error('Wallet not connected');
      }

      const signTransactionFeature = currentMWAWallet.wallet.features['solana:signTransaction'];

      if (!signTransactionFeature) {
        throw new Error('Wallet does not support transaction signing');
      }

      // Serialize transaction
      const serializedTx = transaction.serialize({
        requireAllSignatures: false,
        verifySignatures: false,
      } as any);

      // Sign using Wallet Standard
      const { signedTransactions } = await (signTransactionFeature as any).signTransaction({
        transaction: serializedTx,
      });

      // Deserialize the signed transaction
      if (transaction instanceof VersionedTransaction) {
        return VersionedTransaction.deserialize(signedTransactions[0]) as T;
      } else {
        return Transaction.from(signedTransactions[0]) as T;
      }
    } catch (error) {
      console.error('[MWA] signTransaction error:', error);

      // Report signing errors to Sentry
      captureSentryException(error as Error, {
        operation: 'mwa_sign_transaction',
        walletName: currentMWAWallet.wallet?.name || 'unknown',
        isConnected: currentMWAWallet.isConnected,
        hasPublicKey: currentMWAWallet.publicKey !== null,
        transactionType: 'single',
      });

      throw error;
    }
  },

  async signAllTransactions<T extends Transaction | VersionedTransaction>(
    transactions: T[]
  ): Promise<T[]> {
    try {
      if (!currentMWAWallet.wallet || !currentMWAWallet.isConnected) {
        throw new Error('Wallet not connected');
      }

      const signTransactionFeature = currentMWAWallet.wallet.features['solana:signTransaction'];

      if (!signTransactionFeature) {
        throw new Error('Wallet does not support transaction signing');
      }

      // Serialize all transactions
      const serializedTxs = transactions.map(tx =>
        tx.serialize({
          requireAllSignatures: false,
          verifySignatures: false,
        } as any)
      );

      // Sign all using Wallet Standard
      const results = await Promise.all(
        serializedTxs.map(async (serializedTx) => {
          const { signedTransactions } = await (signTransactionFeature as any).signTransaction({
            transaction: serializedTx,
          });
          return signedTransactions[0];
        })
      );

      // Deserialize signed transactions
      return results.map((signedTx, index) => {
        const originalTx = transactions[index];
        if (originalTx instanceof VersionedTransaction) {
          return VersionedTransaction.deserialize(signedTx) as T;
        } else {
          return Transaction.from(signedTx) as T;
        }
      });
    } catch (error) {
      console.error('[MWA] signAllTransactions error:', error);

      // Report batch signing errors to Sentry
      captureSentryException(error as Error, {
        operation: 'mwa_sign_all_transactions',
        walletName: currentMWAWallet.wallet?.name || 'unknown',
        isConnected: currentMWAWallet.isConnected,
        hasPublicKey: currentMWAWallet.publicKey !== null,
        transactionType: 'batch',
        transactionCount: transactions.length,
      });

      throw error;
    }
  },

  async signMessage(message: Uint8Array): Promise<{ signature: Uint8Array }> {
    try {
      if (!currentMWAWallet.wallet || !currentMWAWallet.isConnected) {
        throw new Error('Wallet not connected');
      }

      const signMessageFeature = currentMWAWallet.wallet.features['solana:signMessage'];

      if (!signMessageFeature) {
        throw new Error('Wallet does not support message signing');
      }

      // Sign message using Wallet Standard
      const { signatures } = await (signMessageFeature as any).signMessage({
        message,
      });

      return { signature: signatures[0] };
    } catch (error) {
      console.error('[MWA] signMessage error:', error);

      // Report message signing errors to Sentry
      captureSentryException(error as Error, {
        operation: 'mwa_sign_message',
        walletName: currentMWAWallet.wallet?.name || 'unknown',
        isConnected: currentMWAWallet.isConnected,
        hasPublicKey: currentMWAWallet.publicKey !== null,
      });

      throw error;
    }
  },

  // Event handlers - Wallet Standard events
  on: (event: string, callback: (args: any) => void) => {
    // Wallet Standard uses a different event model
    // For now, log the event registration for debugging
    console.log(`[MWA] Event listener registered for ${event}`);

    // If we have a connected wallet, we could potentially listen to its events
    // but the Wallet Standard API doesn't expose a standardized event system yet
  },

  off: (event: string, callback: (args: any) => void) => {
    console.log(`[MWA] Event listener removed for ${event}`);
  },
};

// Re-export isMWASupported from mwaConfig for convenience
export { isMWASupported } from './mwaConfig';
